import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { FILE_EXTENSIONS, ALLOWED_FILE_TYPES, FILE_SIZE } from '../../core/constants';
import { PdfPreviewModalComponent, PdfPreviewModalData } from '../pdf-preview-modal/pdf-preview-modal.component';
import { SignStateService } from '../../core/services/sign-state.service';
import { MailService } from '../../services/mail.service';
import { NotificationService } from '../../services/notification.service';
import { UiHelperService } from '../../services/ui-helper.service';
import { MailAttachmentRequest, SendMailRequest } from '../../models/send-mail.model';
import { environment } from '../../../environments/environment';
import Swal from 'sweetalert2';
import { finalize } from 'rxjs';

export interface SignedFilesModalData {
  signedFileSrcs: string[];
  originalFileNames: string[];
  fileType: 'pdf' | 'other';
  remitterName?: string;
  serial?: string;
  transactionId?: string;
  identificationNumber?: string;
}

@Component({
    selector: 'app-signed-files-modal',
    templateUrl: './signed-files-modal.component.html',
    styleUrls: ['./signed-files-modal.component.css'],
    standalone: false
})
export class SignedFilesModalComponent {
  showSendEmailForm = false;
  recipientEmail = '';
  sendFileIndex: number | null = null;
  sendingEmail = false;
  selectedFlags: boolean[] = [];

  constructor(
    public dialogRef: MatDialogRef<SignedFilesModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SignedFilesModalData,
    private dialog: MatDialog,
    private router: Router,
    private signStateService: SignStateService,
    private mailService: MailService,
    private notificationService: NotificationService,
    private uiHelper: UiHelperService
  ) {
    const onlyOne = this.data.signedFileSrcs.length === 1;
    this.selectedFlags = this.data.signedFileSrcs.map(() => onlyOne);
  }

  get selectedCount(): number {
    return this.selectedFlags.filter(Boolean).length;
  }

  get allSelected(): boolean {
    return this.selectedFlags.length > 0 && this.selectedFlags.every(Boolean);
  }

  get selectedIndices(): number[] {
    return this.selectedFlags
      .map((selected, index) => (selected ? index : -1))
      .filter(index => index >= 0);
  }

  get downloadButtonLabel(): string {
    if (this.selectedCount <= 1) {
      return 'Descargar';
    }
    return `Descargar (${this.selectedCount})`;
  }

  /** Hay al menos un firmado ≤ 5 MB (aparece la acción de correo). */
  get showEmailActions(): boolean {
    return this.data.signedFileSrcs.some((_, i) => this.canSendFileByEmail(i));
  }

  /** El documento firmado cabe en el límite de correo (≤ 5 MB). */
  canSendFileByEmail(index: number): boolean {
    const src = this.data.signedFileSrcs[index];
    if (!src) {
      return false;
    }
    return this.estimateDataUrlBytes(src) <= FILE_SIZE.MAX_EMAIL_ATTACHMENT;
  }

  /** Tamaño aproximado en bytes del contenido (data URL Base64 → binario). */
  private estimateDataUrlBytes(src: string): number {
    const raw = src.startsWith('data:')
      ? src.slice(Math.max(0, src.indexOf(',') + 1))
      : src;
    if (!raw) {
      return 0;
    }
    const padding = raw.endsWith('==') ? 2 : raw.endsWith('=') ? 1 : 0;
    return Math.max(0, Math.floor((raw.length * 3) / 4) - padding);
  }

  closeDialog(): void {
    this.signStateService.reset();
    this.dialogRef.close();
    this.router.navigate(['/certitool']);
  }

  isSelected(index: number): boolean {
    return !!this.selectedFlags[index];
  }

  toggleRow(index: number, event?: Event): void {
    event?.stopPropagation();
    this.selectedFlags[index] = !this.selectedFlags[index];
    this.selectedFlags = [...this.selectedFlags];
  }

  toggleSelectAll(event?: Event): void {
    event?.stopPropagation();
    const next = !this.allSelected;
    this.selectedFlags = this.selectedFlags.map(() => next);
  }

  private requireSelection(min = 1): number[] | null {
    const indices = this.selectedIndices;
    if (indices.length < min) {
      Swal.fire({
        ...this.notificationService.getSwalBaseConfig(),
        icon: 'info',
        title: 'Selecciona un documento',
        text: 'Marca en la tabla el documento con el que quieres operar.',
        confirmButtonText: this.notificationService.swalConfirmButtonText,
        customClass: this.notificationService.swalCustomClass
      });
      return null;
    }
    return indices;
  }

  private requireSingleSelection(): number | null {
    const indices = this.requireSelection(1);
    if (!indices) {
      return null;
    }
    if (indices.length > 1) {
      Swal.fire({
        ...this.notificationService.getSwalBaseConfig(),
        icon: 'info',
        title: 'Selecciona solo uno',
        text: 'Esta acción aplica a un documento a la vez. Deja marcada una sola fila.',
        confirmButtonText: this.notificationService.swalConfirmButtonText,
        customClass: this.notificationService.swalCustomClass
      });
      return null;
    }
    return indices[0];
  }

  selectOnly(index: number): void {
    this.selectedFlags = this.selectedFlags.map((_, i) => i === index);
  }

  toggleSendEmailForm(): void {
    if (this.showSendEmailForm) {
      this.closeSendEmailForm();
      return;
    }
    const index = this.requireSingleSelection();
    if (index === null) {
      return;
    }
    if (!this.canSendFileByEmail(index)) {
      return;
    }
    this.openSendEmailForm(index);
  }

  openSendEmailForm(index: number): void {
    if (!this.canSendFileByEmail(index)) {
      return;
    }
    this.selectOnly(index);
    this.sendFileIndex = index;
    this.showSendEmailForm = true;
  }

  closeSendEmailForm(): void {
    this.showSendEmailForm = false;
    this.recipientEmail = '';
    this.sendFileIndex = null;
  }

  getSendTargetLabel(): string {
    const index = this.sendFileIndex ?? this.selectedIndices[0];
    if (index == null) {
      return '';
    }
    return this.data.originalFileNames[index] || `Documento ${index + 1}`;
  }

  isRecipientEmailValid(): boolean {
    const email = this.recipientEmail.trim();
    if (!email) {
      return false;
    }
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  canSendEmail(): boolean {
    return this.isRecipientEmailValid()
      && !this.sendingEmail
      && this.sendFileIndex !== null
      && this.canSendFileByEmail(this.sendFileIndex);
  }

  sendDocument(): void {
    if (!this.canSendEmail() || this.sendFileIndex === null) {
      return;
    }

    const index = this.sendFileIndex;
    if (!this.canSendFileByEmail(index)) {
      return;
    }
    const src = this.data.signedFileSrcs[index];
    const fileName = this.buildAttachmentName(index);
    const parsed = this.parseDataUrl(src);
    if (!parsed) {
      Swal.fire({
        ...this.notificationService.getSwalBaseConfig(),
        icon: 'error',
        title: 'No se pudo leer el archivo',
        text: fileName,
        confirmButtonText: this.notificationService.swalConfirmButtonText,
        customClass: this.notificationService.swalCustomClass
      });
      return;
    }

    const attachments: MailAttachmentRequest[] = [{
      name: fileName,
      mime: parsed.mime,
      data: parsed.data
    }];

    const remitterName =
      this.data.remitterName?.trim()
      || this.signStateService.currentState.selectedCertificate?.cn?.trim()
      || 'CertiTool';

    const email = this.recipientEmail.trim();
    const request: SendMailRequest = {
      recipient: email,
      templateData: {
        nombreDestinatario: this.defaultNombreDestinatario(email),
        nombreRemitente: remitterName,
        nombreDocumento: fileName,
        fechaSolicitud: this.formatFechaSolicitud(new Date()),
        linkFirma: this.defaultLinkFirma()
      },
      attachments,
      serial: this.data.serial || undefined,
      transactionId: this.data.transactionId || undefined,
      identificationNumber: this.data.identificationNumber || undefined
    };

    this.sendingEmail = true;
    this.uiHelper.showSpinner('Enviando documento por correo…', 'admin');
    this.mailService.enviar(request).pipe(
      finalize(() => {
        this.sendingEmail = false;
        this.uiHelper.hideSpinner();
      })
    ).subscribe({
      next: (res) => {
        if (res.success) {
          Swal.fire({
            ...this.notificationService.getSwalBaseConfig(),
            icon: 'success',
            title: 'Correo enviado',
            text: res.message || 'El documento se envió correctamente.',
            confirmButtonText: this.notificationService.swalConfirmButtonText,
            customClass: this.notificationService.swalCustomClass
          });
          this.closeSendEmailForm();
          return;
        }
        Swal.fire({
          ...this.notificationService.getSwalBaseConfig(),
          icon: 'error',
          title: 'No se pudo enviar',
          text: res.message || 'Intente nuevamente.',
          confirmButtonText: this.notificationService.swalConfirmButtonText,
          customClass: this.notificationService.swalCustomClass
        });
      },
      error: (err) => {
        const msg = err?.error?.message || err?.message || 'Error al enviar el correo.';
        Swal.fire({
          ...this.notificationService.getSwalBaseConfig(),
          icon: 'error',
          title: 'No se pudo enviar',
          text: msg,
          confirmButtonText: this.notificationService.swalConfirmButtonText,
          customClass: this.notificationService.swalCustomClass
        });
      }
    });
  }

  private defaultNombreDestinatario(email: string): string {
    const local = email.split('@')[0]?.trim();
    return local || email;
  }

  private defaultLinkFirma(): string {
    const configured = (environment as { defaultLinkFirma?: string }).defaultLinkFirma?.trim();
    if (configured) {
      return configured;
    }
    return `${window.location.origin}/certitool`;
  }

  private buildAttachmentName(index: number): string {
    let originalName = this.data.originalFileNames[index] || `SignedDocument${index + 1}`;
    if (this.data.fileType === 'other') {
      const lower = originalName.toLowerCase();
      const hasCadesExt = ALLOWED_FILE_TYPES.CADES.extensions.some((ext) =>
        lower.endsWith(ext)
      );
      if (!hasCadesExt && !lower.includes('_firmado.')) {
        originalName += FILE_EXTENSIONS.P7Z;
      }
    } else if (this.data.fileType === 'pdf' && !originalName.toLowerCase().endsWith('.pdf')) {
      originalName += '.pdf';
    }
    return `Signed_${originalName}`;
  }

  private parseDataUrl(src: string): { mime: string; data: string } | null {
    if (!src) {
      return null;
    }
    if (src.startsWith('data:')) {
      const comma = src.indexOf(',');
      if (comma < 0) {
        return null;
      }
      const meta = src.substring(5, comma);
      const data = src.substring(comma + 1);
      const mime = meta.split(';')[0] || this.defaultMime();
      return { mime, data };
    }
    return { mime: this.defaultMime(), data: src };
  }

  private defaultMime(): string {
    return this.data.fileType === 'pdf' ? 'application/pdf' : 'application/octet-stream';
  }

  private formatFechaSolicitud(date: Date): string {
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  }

  viewSelected(): void {
    const index = this.requireSingleSelection();
    if (index === null) {
      return;
    }
    this.viewPdf(index);
  }

  downloadSelected(): void {
    const indices = this.requireSelection(1);
    if (!indices) {
      return;
    }
    indices.forEach((index, order) => {
      setTimeout(() => this.downloadFile(index), order * 300);
    });
  }

  downloadFile(index: number): void {
    const src = this.data.signedFileSrcs[index];
    let originalName = this.data.originalFileNames[index] || `SignedDocument${index + 1}`;

    if (this.data.fileType === 'other') {
      const lower = originalName.toLowerCase();
      const hasCadesExt = ALLOWED_FILE_TYPES.CADES.extensions.some((ext) =>
        lower.endsWith(ext)
      );
      if (!hasCadesExt && !lower.includes('_firmado.')) {
        originalName += FILE_EXTENSIONS.P7Z;
      }
    } else if (this.data.fileType === 'pdf' && !originalName.toLowerCase().endsWith('.pdf')) {
      originalName += '.pdf';
    }

    const downloadName = `Signed_${originalName}`;
    const link = document.createElement('a');
    link.href = src;
    link.download = downloadName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  viewPdf(index: number): void {
    if (this.data.fileType !== 'pdf') {
      return;
    }
    this.selectOnly(index);
    const pdfSrc = this.data.signedFileSrcs[index];
    const fileName = this.data.originalFileNames[index] || `Documento${index + 1}.pdf`;

    const dialogData: PdfPreviewModalData = {
      pdfSrc: pdfSrc,
      fileName: fileName
    };

    this.dialog.open(PdfPreviewModalComponent, {
      width: '75vw',
      height: '75vh',
      maxWidth: '75vw',
      maxHeight: '75vh',
      data: dialogData,
      panelClass: 'pdf-preview-dialog',
      hasBackdrop: true,
      disableClose: true,
      closeOnNavigation: false
    });
  }

  downloadRow(index: number): void {
    this.selectOnly(index);
    this.downloadFile(index);
  }

  getFileIcon(fileType: 'pdf' | 'other'): string {
    return fileType === 'pdf' ? '📄' : '📦';
  }

  getFileTypeLabel(fileType: 'pdf' | 'other'): string {
    return fileType === 'pdf'
      ? 'PDF'
      : `CAdES (${ALLOWED_FILE_TYPES.CADES.extensions.join(', ')})`;
  }
}
